import { useState, useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Lock } from 'lucide-react'
import { useBreadcrumb } from '../hooks/useBreadcrumb'
import { useSimpleToast } from '../hooks/useToast'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Textarea } from '../components/ui/textarea'
import { Skeleton } from '../components/ui/skeleton'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import WheelPickerField from '../components/climbs/WheelPickerField'
import {
  ExerciseTemplateEditor,
  type ExerciseTemplateEditorHandle,
} from '../components/training-diary/ExerciseTemplateEditor'
import {
  emptyFormData,
  exerciseToFormData,
  formToPayload,
  formatSeconds,
  isValid,
  metadataPayload,
  totalDurationSeconds,
  validateForm,
  type CustomExerciseFormData,
  type FieldErrors,
} from '../components/training-diary/custom-exercise-form'
import { exercisesService } from '../services/exercises'
import { exerciseCategoriesService } from '../services/exercise-categories'
import type { WheelPickerOption } from '../components/ui/wheel-picker'
import { EXERCISE_EQUIPMENTS, type ExerciseCategory, type ExerciseEquipment } from '../types/exercise'
import type { ExerciseTemplateInput } from '../types/exercise-template'

const EQUIPMENT_NONE = '__none__'

const DURATION_OPTIONS: WheelPickerOption<number>[] = [
  0,
  ...Array.from({ length: 48 }, (_, i) => (i + 1) * 5),
].map((minutes) => ({ value: minutes, label: minutes === 0 ? '—' : `${minutes}` }))

/**
 * Creates and edits a user's private exercise.
 *
 * Creating is the details alone: title, category, kit, how long it usually
 * takes. The exercise exists from that moment, which is what lets the blocks
 * below it be saved one at a time — a block is an ExerciseTemplate with an id
 * of its own, and there is nothing to attach one to until the exercise is
 * there. Duplicating is the exception that writes both at once: the source's
 * blocks travel with it in the create payload.
 */
export default function CustomExerciseFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const { showError, showSuccess } = useSimpleToast()

  const isEdit = Boolean(id)
  const duplicateFrom = searchParams.get('duplicate_from')
  const presetTitle = searchParams.get('title') ?? ''

  const [form, setForm] = useState<CustomExerciseFormData | null>(null)
  const [categories, setCategories] = useState<ExerciseCategory[]>([])
  const [errors, setErrors] = useState<FieldErrors>({})
  const [submitted, setSubmitted] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmNoBlocks, setConfirmNoBlocks] = useState(false)
  const blocksRef = useRef<ExerciseTemplateEditorHandle>(null)

  useBreadcrumb(
    () => ({
      onBack: () => navigate('/training-diary/exercises'),
      segments: [
        {
          label: t('trainingDiary.customExercise.pageTitle', 'My workouts'),
          onClick: () => navigate('/training-diary/exercises'),
        },
        {
          label: isEdit
            ? t('trainingDiary.customExercise.editTitle', 'Edit')
            : t('trainingDiary.customExercise.newTitle', 'New'),
          muted: true,
        },
      ],
    }),
    [t, navigate, isEdit]
  )

  useEffect(() => {
    exerciseCategoriesService.list().then(setCategories).catch(() => {})
  }, [])

  useEffect(() => {
    const sourceId = id ?? duplicateFrom
    if (!sourceId) {
      setForm(emptyFormData(presetTitle))
      return
    }

    exercisesService
      .show(sourceId)
      .then((exercise) => {
        const data = exerciseToFormData(exercise)
        // A duplicate is a new exercise, and two identical titles in the picker
        // help nobody.
        setForm(
          duplicateFrom && !id
            ? { ...data, title: t('trainingDiary.customExercise.copyTitle', '{{title}} (copy)', { title: data.title }) }
            : data
        )
      })
      .catch(() => {
        showError(t('trainingDiary.customExercise.loadError', 'Could not load this workout'))
        navigate('/training-diary/exercises')
      })
  }, [id, duplicateFrom, presetTitle, navigate, showError, t])

  const update = useCallback((patch: Partial<CustomExerciseFormData>) => {
    setForm((prev) => (prev ? { ...prev, ...patch } : prev))
  }, [])

  const setTemplates = useCallback((templates: ExerciseTemplateInput[]) => {
    setForm((prev) => (prev ? { ...prev, templates } : prev))
  }, [])

  // Errors appear only after the first submit, then track every keystroke.
  useEffect(() => {
    if (!submitted || !form) return
    setErrors(validateForm(form))
  }, [form, submitted])

  /** The details step: the exercise is born here, blocks come after. */
  async function create(data: CustomExerciseFormData) {
    setSaving(true)
    try {
      // Duplicating is the one create that carries blocks; a blank one starts
      // with none by design.
      const created = await exercisesService.create(formToPayload(data))
      showSuccess(t('trainingDiary.customExercise.created', 'Workout created'))
      navigate(`/training-diary/exercises/${created.id}/edit`, { replace: true })
    } catch {
      showError(t('trainingDiary.customExercise.saveError', 'Could not save this workout'))
      setSaving(false)
    }
  }

  /** The details of an exercise that already exists. Blocks save themselves. */
  async function saveDetails(data: CustomExerciseFormData) {
    if (!id) return
    setSaving(true)
    try {
      // The open block first, so "Save" means everything on the page.
      if (!(await blocksRef.current?.flush())) {
        setSaving(false)
        return
      }
      await exercisesService.update(id, metadataPayload(data))
      showSuccess(t('trainingDiary.customExercise.updated', 'Workout updated'))
      navigate('/training-diary/exercises')
    } catch {
      showError(t('trainingDiary.customExercise.saveError', 'Could not save this workout'))
      setSaving(false)
    }
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    if (!form) return

    setSubmitted(true)
    const found = validateForm(form)
    setErrors(found)
    if (!isValid(found)) return

    if (!isEdit) return create(form)

    // A workout with no blocks is legitimate ("Boulder session, 90'") but it
    // cannot be run with the timer, so say so once before leaving it that way.
    if (form.templates.length === 0) {
      setConfirmNoBlocks(true)
      return
    }

    saveDetails(form)
  }

  if (!form) {
    return (
      <div className="flex flex-col gap-4 max-w-2xl mx-auto p-4">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    )
  }

  const totalSeconds = totalDurationSeconds(form.templates)
  const durationValue = Number.parseInt(form.durationMinutes, 10)

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-6 max-w-2xl mx-auto p-4 pb-12">
      <header className="flex flex-col gap-1.5">
        <h1 className="text-2xl font-semibold leading-tight">
          {isEdit
            ? t('trainingDiary.customExercise.editHeading', 'Edit workout')
            : t('trainingDiary.customExercise.newHeading', 'New custom workout')}
        </h1>
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Lock className="size-3.5 shrink-0" />
          {t(
            'trainingDiary.customExercise.privacyNote',
            'Private to you. Coaches can assign their own workouts through a training plan.'
          )}
        </p>
      </header>

      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="title" className="text-xs">
            {t('trainingDiary.customExercise.fields.title', 'Title')}
          </Label>
          <Input
            id="title"
            value={form.title}
            onChange={(e) => update({ title: e.target.value })}
            placeholder={t('trainingDiary.customExercise.dialog.titlePlaceholder', 'e.g. Max hangs 92%')}
          />
          {errors.title && (
            <p className="text-[11px] text-destructive">
              {t(`trainingDiary.customExercise.errors.${errors.title}`, errors.title)}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="category" className="text-xs">
              {t('trainingDiary.customExercise.fields.category', 'Category')}
            </Label>
            <Select value={form.categoryId} onValueChange={(categoryId) => update({ categoryId })}>
              <SelectTrigger id="category" className="h-9 text-sm">
                <SelectValue
                  placeholder={t('trainingDiary.customExercise.fields.categoryPlaceholder', 'Choose…')}
                />
              </SelectTrigger>
              <SelectContent>
                {categories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.categoryId && (
              <p className="text-[11px] text-destructive">
                {t(`trainingDiary.customExercise.errors.${errors.categoryId}`, errors.categoryId)}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="equipment" className="text-xs">
              {t('trainingDiary.customExercise.fields.equipment', 'Equipment')}
            </Label>
            <Select
              value={form.equipment || EQUIPMENT_NONE}
              onValueChange={(value) =>
                update({ equipment: value === EQUIPMENT_NONE ? '' : (value as ExerciseEquipment) })
              }
            >
              <SelectTrigger id="equipment" className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={EQUIPMENT_NONE}>
                  {t('trainingDiary.customExercise.fields.equipmentUnset', 'Not specified')}
                </SelectItem>
                {EXERCISE_EQUIPMENTS.map((equipment) => (
                  <SelectItem key={equipment} value={equipment}>
                    {t(`trainingDiary.customExercise.equipment.${equipment}`, equipment)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-xs">
            {t('trainingDiary.customExercise.fields.duration', 'Typical duration (min)')}
          </Label>
          <WheelPickerField
            label={t('trainingDiary.customExercise.fields.duration', 'Typical duration (min)')}
            placeholder={
              totalSeconds != null && totalSeconds > 0
                ? t('trainingDiary.customExercise.fields.durationHint', 'Blocks add up to {{duration}}', {
                    duration: formatSeconds(totalSeconds),
                  })
                : t('common.notSet', '—')
            }
            value={Number.isFinite(durationValue) ? durationValue : null}
            options={DURATION_OPTIONS}
            onChange={(minutes) => update({ durationMinutes: minutes > 0 ? String(minutes) : '' })}
            className="w-fit"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="description" className="text-xs">
            {t('trainingDiary.customExercise.fields.description', 'Notes')}
          </Label>
          <Textarea
            id="description"
            rows={3}
            value={form.description}
            onChange={(e) => update({ description: e.target.value })}
            placeholder={t(
              'trainingDiary.customExercise.fields.descriptionPlaceholder',
              'How to run it, cues, what to watch for…'
            )}
          />
        </div>
      </section>

      {isEdit && id ? (
        <ExerciseTemplateEditor
          ref={blocksRef}
          exerciseId={id}
          templates={form.templates}
          onTemplatesChange={setTemplates}
        />
      ) : (
        <p className="rounded-lg border border-dashed p-4 text-xs text-muted-foreground text-center">
          {t(
            'trainingDiary.customExercise.blocksAfterCreate',
            'Save the workout first — its blocks are added next.'
          )}
        </p>
      )}

      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={() => navigate(-1)}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button type="submit" disabled={saving}>
          {saving
            ? t('common.saving', 'Saving…')
            : isEdit
              ? t('common.save', 'Save')
              : t('trainingDiary.customExercise.createButton', 'Create')}
        </Button>
      </div>

      <AlertDialog open={confirmNoBlocks} onOpenChange={setConfirmNoBlocks}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('trainingDiary.customExercise.noBlocks.title', 'Save without a timer protocol?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                'trainingDiary.customExercise.noBlocks.body',
                'This workout has no blocks, so it cannot be run with the timer and you will log its result by hand. You can add blocks later.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('trainingDiary.customExercise.noBlocks.cancel', 'Add blocks')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => saveDetails(form)}>
              {t('trainingDiary.customExercise.noBlocks.confirm', 'Save anyway')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  )
}
